import {InfoIcon} from '@primer/octicons-react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {TemplateListPaneQuery} from './__generated__/TemplateListPaneQuery.graphql'
import {LABELS} from './constants/labels'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import type {RepositoryNode} from './template-list-query-types'
import {TemplateList, type TemplateListSelectedProp, type TemplateListSharedProps} from './TemplateList'
import styles from './TemplateListPane.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const TemplateListPaneGraphQLQuery = graphql`
  query TemplateListPaneQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        hasIssuesEnabled
        id
        nameWithOwner
        issueForms {
          __typename
          filename
          name
          description
        }
        issueTemplates {
          __typename
          filename
          name
          about
        }
        isBlankIssuesEnabled
        isBlankIssuesMaintainerOverride
        isSecurityPolicyEnabled
        securityPolicyUrl
        contactLinks {
          name
          about
          url
        }
        hasAnyTemplates
      }
    }
  }
`

export type TemplateListPaneFragment = Pick<
  RepositoryNode,
  | 'hasIssuesEnabled'
  | 'id'
  | 'nameWithOwner'
  | 'issueForms'
  | 'issueTemplates'
  | 'isBlankIssuesEnabled'
  | 'isBlankIssuesMaintainerOverride'
  | 'isSecurityPolicyEnabled'
  | 'securityPolicyUrl'
  | 'contactLinks'
  | 'hasAnyTemplates'
>

export type TemplateListPaneInternalProps = {
  repository: TemplateListPaneFragment
} & TemplateListPaneSharedProps

type TemplateListPaneSharedProps = {
  /** Id to use on validation messages
   *
   * `TemplateListNewPane` might be used with a repository that has issues disabled or has no templates available.
   * When this is the case, a message is shown and rendered with the id passed in this property.
   *
   * Consumers can then use this id in conjuction with an `aria-describedby` to ensure their control is properly
   * described with the message and accessible.
   */
  descriptionId?: string
} & TemplateListSelectedProp &
  TemplateListSharedProps

type TemplateListPaneFromRepoProps = {
  repositoryId: string
} & TemplateListPaneSharedProps

export function TemplateListPaneFromRepo({repositoryId, ...props}: TemplateListPaneFromRepoProps) {
  const data = useLazyLoadQuery<TemplateListPaneQuery>(TemplateListPaneGraphQLQuery, {
    id: repositoryId,
  })
  if (!data.node) return null

  // RELAY-CLEANUP(TemplateListPaneQuery): narrows the still-Relay open-union node to the Repository fields.
  // Remove when TemplateListPaneQuery leaves Relay.
  return <TemplateListInternal repository={data.node as TemplateListPaneFragment} {...props} />
}

export function TemplateListInternal({repository, descriptionId, ...props}: TemplateListPaneInternalProps) {
  const {optionConfig} = useIssueCreateConfigContext()
  const {insidePortal} = optionConfig

  return (
    <div>
      {repository.hasIssuesEnabled ? (
        <TemplateList
          repository={repository}
          {...props}
          className={`${
            !insidePortal
              ? `border borderColor-muted rounded-2 overflow-hidden
                ${optionConfig.showRepositoryPicker ? 'pt-0 mt-0' : 'py-2 mt-2'}`
              : ``
          }`}
        />
      ) : (
        <div className={`${styles.disabledStateGap} d-flex flex-row flex-items-center tmp-m-3`}>
          <InfoIcon />
          <span id={descriptionId}>{LABELS.issuesDisabledForRepo}</span>
        </div>
      )}
    </div>
  )
}
